import { IWidget, WidgetType } from '../widget/Widget';
import { flatten, startCase } from 'lodash';
import { WidgetInputType } from '../widget/input/WidgetInputType';
import { createMetadata, IMetadata, partialMetadata } from './Metadata';
import { MetadataType } from './MetadataType';
import { IProcedure } from '../Procedure';
import { Request } from 'server/schema/procedure/request/Request.entity';

export class MetadataLogic {
  /**
   * Ottiene i metadati contenuti in un Widget
   */
  static fromWidget(widget: IWidget) {
    switch (widget.type) {
      case WidgetType.Input:
        return widget.input?.metadata ?? [];

      case WidgetType.Dynamic:
        return widget.dynamic?.metadata ? [widget.dynamic?.metadata] : [];

      default:
        return [];
    }
  }

  /**
   * Ottiene l'id metadato del Widget di tipo MultiPreference
   */
  static fromMultiPreferenceWidget(widget: IWidget) {
    switch (widget.input?.type) {
      case WidgetInputType.MultiPreference:
        return widget.input?.metadata ?? [];

      default:
        return [];
    }
  }

  /**
   * Ottiene i metadati del metadato lista
   */
  static fromSubwidgets(widget: IWidget) {
    if (
      widget.input?.type !== WidgetInputType.List &&
      widget.input?.type !== WidgetInputType.MultiPreference
    ) {
      throw new Error(`L'elenco dei sub-widget è disponibile solo per Widget di tipo Lista`); // prettier-ignore
    }

    // Nel caso di Widget multi-preferenza, non abbiamo dei veri sotto-widget, ma solamente le colonne del CSV caricato dall'utente.
    // Per uniformare la gestione del codice in fase di stampa, ritorniamo le colonne trasformandole in modo "fittizio" in metadati,
    // in modo che l'algoritmo le possa trattare allo stesso modo.
    // Questo funziona perché il formato di salvataggio dei dati delle liste e delle multi-preferenze nella Request è uguale,
    // ovvero un array di rows di tipo { values: { ... } }
    if (widget.input?.type === WidgetInputType.MultiPreference) {
      const fieldsAsMetadata = widget.input.extra?.cascade?.fields.map(
        field => {
          return {
            id: field.index,
            name: field.label,
            datatype: MetadataType.Text
          } as any;
        }
      );
      return fieldsAsMetadata ?? [];
    }
    const subwidgets = widget.input.subwidgets ?? [];
    return flatten(subwidgets.map(widget => MetadataLogic.fromWidget(widget)));
  }

  /**
   * Ottiene l'elenco dei metadati annidati in un altro, ricercando nei Widget.
   */
  static getSubInProcedure(procedure: IProcedure, id: string) {
    for (const widget of procedure.widgets) {
      const metadatas = MetadataLogic.fromWidget(widget);
      const isTarget = metadatas.some(m => m.id === id);

      if (isTarget) return MetadataLogic.fromSubwidgets(widget);
    }

    throw new Error(`Metadato "${id}" non trovato nella domanda ${procedure.id}. (Codice ML01)`); // prettier-ignore
  }

  /**
   * Ottiene i metadati contenuti in una Domanda
   */
  static fromProcedure(procedure: IProcedure) {
    return flatten(
      procedure.widgets.map(widget => MetadataLogic.fromWidget(widget))
    );
  }

  /**
   *  Ritorna l'informazione del metadato ed il widget a cui appartiene
   */
  static fromProcedureWithWidget(procedure: IProcedure) {
    return flatten(
      procedure.widgets.map(widget =>
        MetadataLogic.fromWidget(widget).map(m => ({ widget, metadata: m }))
      )
    );
  }

  /**
   * Ottiene i metadati contenuti in un insieme di Widget
   */
  static fromWidgets(widgets: IWidget[]) {
    return flatten(widgets.map(widget => MetadataLogic.fromWidget(widget)));
  }

  /**
   * Trova un metadato in una domanda
   */
  static getInProcedure(procedure: IProcedure, id: string) {
    for (const widget of procedure.widgets) {
      const metadatas = MetadataLogic.fromWidget(widget);
      const metadata = metadatas.find(m => m.id === id);
      if (metadata) return metadata;
    }

    throw new Error(`Metadato "${id}" non trovato nella domanda ${procedure.id}. (Codice ML02)`); // prettier-ignore
  }

  /**
   * Trova il widget e il metadato a partire da un elenco di Widget
   * e l'ID del metadato
   */
  static getWidget(widgets: IWidget[], id: string | undefined) {
    if (!id) {
      return { widget: null, metadata: null };
    }

    for (const widget of widgets) {
      const metadatas = MetadataLogic.fromWidget(widget);
      const metadata = metadatas.find(m => m.id === id);
      if (metadata) {
        return { widget, metadata };
      }
    }

    throw new Error(`Metadato "${id}" non trovato nella domanda. (Codice ML03)`); // prettier-ignore
  }

  /**
   * Ottiene il primo metadato del Widget. Se ce ne sono più di uno, lancia
   * un errore
   */
  static singleFromWidget(widget: IWidget) {
    switch (widget.type) {
      case WidgetType.Input:
        if (widget.input!.metadata.length !== 1) {
          console.error('Numero di metadati non coerente: (widget) ', widget);
          throw new Error(`Numero di metadati non coerente`);
        }
        return widget.input!.metadata[0];

      case WidgetType.Dynamic:
        return widget.dynamic!.metadata;

      default:
        throw new Error('Widget non gestito');
    }
  }

  /**
   * Ottiene un insieme di metadati in base ai nomi specificati.
   */
  static multipleFromWidget(widget: IWidget, codes: string[]) {
    if (widget.type !== WidgetType.Input) throw new Error('Non gestito');

    const metadatas = codes.map(code => {
      const metadata = widget.input!.metadata.find(m => m.code === code);
      if (metadata == null)
        throw new Error(`Metadato "${name}" non trovato per il Widget`);

      return metadata;
    });

    return metadatas;
  }

  /**
   * Ottiene un metadato in un Widget a partire dal suo ID
   */
  static getInWidget(widget: IWidget, id: string) {
    return widget.input!.metadata.find(m => m.id === id);
  }

  /**
   * Ottiene il nome del field di Formik per un metadato
   */
  static getFormName(widget: IWidget, metadata: IMetadata) {
    switch (widget.type) {
      case WidgetType.Input:
        const index = widget.input!.metadata.indexOf(metadata);
        return `input.metadata[${index}]`;

      case WidgetType.Dynamic:
        return `dynamic.metadata`;

      default:
        throw new Error('Widget non gestito');
    }
  }

  /**
   * Ottiene il nome del metadato
   */
  static getName(widget: IWidget, code?: string) {
    const name =
      widget.input?.name ?? widget.dynamic?.name ?? `[Widget ${widget.id}]`;

    if (!code) return name;
    return `${name ?? ''} (${startCase(code)})`;
  }

  static createOnWidget(widget: IWidget) {
    // if (!widget.justCreated) return;

    // Gestione metadati collegati all'input
    if (widget.type === WidgetType.Input) {
      const input = widget.input!;

      switch (input.type) {
        case WidgetInputType.Text:
        // case WidgetInputType.Address: // TODO da implementere?
        case WidgetInputType.Email:
        case WidgetInputType.SingleChoice:
        case WidgetInputType.MultipleChoice:
          input.metadata = [partialMetadata(MetadataType.Text)];
          break;

        case WidgetInputType.Region:
          input.metadata = [
            partialMetadata(MetadataType.Text, 'provincia'), // prettier-ignore
            partialMetadata(MetadataType.Text, 'comune')
          ];
          break;

        case WidgetInputType.Date:
          input.metadata = [partialMetadata(MetadataType.Date)];
          break;

        case WidgetInputType.DateInterval:
          input.metadata = [
            partialMetadata(MetadataType.Date, 'inizio'),
            partialMetadata(MetadataType.Date, 'fine')
          ];
          break;

        case WidgetInputType.Currency:
          input.metadata = [partialMetadata(MetadataType.Currency)];
          break;

        case WidgetInputType.Numeric:
          input.metadata = [partialMetadata(MetadataType.Number)];
          break;

        case WidgetInputType.Boolean:
          input.metadata = [partialMetadata(MetadataType.Boolean)];
          break;

        case WidgetInputType.File:
        case WidgetInputType.UploadSigned:
          input.metadata = [partialMetadata(MetadataType.File)];
          break;

        case WidgetInputType.List:
          input.metadata = [partialMetadata(MetadataType.Array)];
          break;

        case WidgetInputType.Captcha:
          input.metadata = [partialMetadata(MetadataType.Text)];
          break;

        default:
          break;
      }

      // Sincronizzo l'ID della domanda per i singoli metadati
      if (input.metadata) {
        input.metadata.forEach(m => {
          m.procedureId = widget.procedureId;
          m.name = MetadataLogic.getName(widget, m.code);
        });
      }
    }

    // Gestione metadati collegati al dynamic
    if (widget.type === WidgetType.Dynamic) {
      const dynamic = widget.dynamic!;
      const mainName = dynamic.label;

      dynamic.metadata = createMetadata(
        mainName,
        MetadataType.Text,
        dynamic.label
      );
      dynamic.metadata.procedureId = widget.procedureId;
    }
  }

  /**
   * Aggiorna il nome dei metadati a partire dall'input.
   */
  static updateName(widget: IWidget) {
    MetadataLogic.fromWidget(widget).forEach(metadata => {
      metadata.name = MetadataLogic.getName(widget, metadata.code);
    });
  }

  /**
   * Ottiene il WidgetInput o WidgetDynamic collegato al metadato direttamente
   * dalla relazione su DB
   */
  static getInternalConnectedWidget(metadata: IMetadata) {
    return metadata.input ?? metadata.dynamic;
  }
}
